/**
 * Pendo utility functions for user behavior tracking and analytics
 * Follows the same pattern as Sentry integration for consistency
 */

// Extend Window interface to include Pendo
declare global {
  interface Window {
    pendo?: {
      initialize: (options: {
        visitor: {
          id: string;
          email?: string;
          full_name?: string;
          [key: string]: unknown;
        };
        account?: {
          id: string;
          [key: string]: unknown;
        };
        [key: string]: unknown;
      }) => void;
      track: (eventName: string, metadata?: Record<string, unknown>) => void;
      updateOptions: (options: {
        visitor?: {
          id: string;
          email?: string;
          full_name?: string;
          [key: string]: unknown;
        };
        account?: {
          id: string;
          [key: string]: unknown;
        };
      }) => void;
      isReady: () => boolean;
    };
  }
}

/**
 * Check if Pendo has been loaded and is available
 * @returns true if Pendo is available, false otherwise
 */
export function isPendoAvailable(): boolean {
  return typeof window !== "undefined" && typeof window.pendo !== "undefined";
}

/**
 * Check if Pendo has been initialized
 * @returns true if Pendo is initialized, false otherwise
 */
export function isPendoInitialized(): boolean {
  if (!isPendoAvailable()) {
    return false;
  }
  try {
    return window.pendo?.isReady() ?? false;
  } catch {
    return false;
  }
}

/**
 * Load Pendo Agent script dynamically
 * @param apiKey - Pendo API key
 * @returns Promise that resolves when script is loaded
 */
export function loadPendoScript(apiKey: string): Promise<void> {
  return new Promise((resolve, reject) => {
    // Check if script is already loaded
    if (isPendoAvailable()) {
      resolve();
      return;
    }

    // Check if script is already being loaded
    const existingScript = document.querySelector(
      `script[src*="cdn.pendo.io"]`
    );
    if (existingScript) {
      // Wait for existing script to load
      existingScript.addEventListener("load", () => resolve());
      existingScript.addEventListener("error", () => reject(new Error("Failed to load Pendo script")));
      return;
    }

    // Create and load script
    const script = document.createElement("script");
    script.src = `https://cdn.pendo.io/agent/static/${apiKey}/pendo.js`;
    script.async = true;
    script.onload = () => {
      // Wait a bit for pendo to be fully available
      const checkPendo = setInterval(() => {
        if (isPendoAvailable()) {
          clearInterval(checkPendo);
          resolve();
        }
      }, 50);

      // Timeout after 5 seconds
      setTimeout(() => {
        clearInterval(checkPendo);
        if (!isPendoAvailable()) {
          reject(new Error("Pendo script loaded but pendo object not available"));
        }
      }, 5000);
    };
    script.onerror = () => {
      reject(new Error("Failed to load Pendo script"));
    };
    document.head.appendChild(script);
  });
}

/**
 * Initialize Pendo with visitor and account data
 * @param options - Pendo initialization options
 */
export function initializePendo(options: {
  visitor: {
    id: string;
    email?: string;
    full_name?: string;
    locale?: string;
    [key: string]: unknown;
  };
  account?: {
    id: string;
    [key: string]: unknown;
  };
}): void {
  if (!isPendoAvailable()) {
    if (import.meta.env.DEV) {
      console.warn("Pendo not available, cannot initialize");
    }
    return;
  }

  try {
    window.pendo?.initialize({
      visitor: options.visitor,
      account: options.account,
    });
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error("Error initializing Pendo:", error);
    }
  }
}

/**
 * Safely set Pendo visitor data
 * Only sets visitor if Pendo is initialized
 * @param visitor - Visitor information to set
 */
export function setPendoVisitor(visitor: {
  id: string;
  email?: string;
  full_name?: string;
  locale?: string;
  timezone?: string;
  [key: string]: unknown;
}): void {
  const { id, ...restVisitor } = visitor;

  if (!isPendoInitialized()) {
    if (import.meta.env.DEV) {
      console.warn("Pendo not initialized, visitor data not set");
    }
    return;
  }

  try {
    window.pendo?.updateOptions({
      visitor: {
        id,
        email: visitor.email,
        full_name: visitor.full_name,
        locale: visitor.locale,
        timezone: visitor.timezone,
        ...restVisitor,
      },
    });
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error("Error setting Pendo visitor:", error);
    }
  }
}

/**
 * Safely set Pendo account data
 * Only sets account if Pendo is initialized
 * @param account - Account information to set
 */
export function setPendoAccount(account: {
  id: string;
  [key: string]: unknown;
}): void {
  const { id, ...restAccount } = account;

  if (!isPendoInitialized()) {
    if (import.meta.env.DEV) {
      console.warn("Pendo not initialized, account data not set");
    }
    return;
  }

  try {
    window.pendo?.updateOptions({
      account: {
        id,
        ...restAccount,
      },
    });
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error("Error setting Pendo account:", error);
    }
  }
}

/**
 * Track a custom event in Pendo
 * Only tracks if Pendo is initialized
 * @param eventName - Name of the event to track
 * @param metadata - Optional metadata to attach to the event
 */
export function trackPendoEvent(
  eventName: string,
  metadata?: Record<string, unknown>
): void {
  if (!isPendoInitialized()) {
    if (import.meta.env.DEV) {
      console.warn("Pendo not initialized, event not tracked:", eventName);
    }
    return;
  }

  try {
    window.pendo?.track(eventName, metadata);
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error("Error tracking Pendo event:", error);
    }
  }
}
